import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { jwtDecode } from "jwt-decode";

// Config object for AWS authentication
const AuthConfig = {
  identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID || '',
  region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID || '',
};

// Use a safer reference pattern to avoid circular dependencies
let refreshTokensFunction = null;
// Track refresh in progress to prevent loops
let refreshInProgress = false;
// Track last refresh timestamp
let lastRefreshTimestamp = 0;

/**
 * Register the refreshTokens function for later use
 * This avoids circular dependencies when importing
 * @param {Function} refreshFn - Function to refresh tokens
 */
export function registerRefreshTokensFunction(refreshFn) {
  refreshTokensFunction = refreshFn;
}

/**
 * Get temporary AWS credentials from Cognito Identity Pool
 * @param {Object} options - Configuration options
 * @returns {Promise<Object>} - Temporary AWS credentials
 */
export async function getTemporaryCredentials(options = {}) {
  // Add trace to help debug credential call sources
  console.log(`[DEBUG CREDS] getTemporaryCredentials called from:`, 
    new Error().stack.split('\n').slice(2, 5).join('\n'));
    
  const identityPoolId = options.identityPoolId || AuthConfig.identityPoolId;
  const region = options.region || AuthConfig.region;
  const userPoolId = options.userPoolId || AuthConfig.userPoolId;
  
  if (!identityPoolId) {
    throw new Error('Identity Pool ID is required. Set REACT_APP_COGNITO_IDENTITY_POOL_ID in your .env file.');
  }
  
  try {
    // 1) Read tokens from storage (prefer localStorage for persistence)
    const storedTokens = getAuthTokens();

    // 2) Prefer an externally passed token if available
    const idToken = options.idToken || storedTokens?.idToken;
    
    // Check if token is missing
    if (!idToken) {
      throw new Error('ID token is missing - user must be authenticated');
    }
    
    // Check if token is expired and trigger refresh if needed
    const now = Date.now();
    const minRefreshInterval = 30000; // 30 seconds minimum between refreshes

    if (isTokenExpired(idToken) && !options.isRefreshing) {
      // If we're already refreshing or it's been less than 30 seconds since last refresh, avoid refreshing again
      if (refreshInProgress || (now - lastRefreshTimestamp < minRefreshInterval)) {
        console.log('[DEBUG CREDS] Refresh already in progress or too recent, using current token');
      } else {
        console.log('[DEBUG CREDS] Token is expired, attempting refresh...');
        refreshInProgress = true;
        
        try {
          // Try to refresh using the registered refresh function
          if (refreshTokensFunction) {
            const refreshed = await refreshTokensFunction({ isRefreshing: true });
            refreshInProgress = false;
            lastRefreshTimestamp = now;
            
            if (refreshed && refreshed.idToken) {
              // Use the freshly minted token
              return await getTemporaryCredentials({
                ...options,
                idToken: refreshed.idToken,
                isRefreshing: true
              });
            }
          } 
          // Fallback to window._authContextRef if available
          else if (typeof window !== 'undefined' && window._authContextRef?.current?.refreshTokens) {
            const refreshed = await window._authContextRef.current.refreshTokens({ isRefreshing: true });
            refreshInProgress = false;
            lastRefreshTimestamp = now;
            
            if (refreshed && refreshed.idToken) {
              // Use the freshly minted token
              return await getTemporaryCredentials({
                ...options,
                idToken: refreshed.idToken,
                isRefreshing: true
              });
            }
          }
          
          // If we reach this point, refresh failed, throw error
          refreshInProgress = false;
          throw new Error('Failed to refresh expired tokens');
        } catch (refreshError) {
          refreshInProgress = false;
          console.error('Token refresh failed:', refreshError);
          throw new Error('Authentication required. Please log in again.');
        }
      }
    }
    
    // Configure the credentials provider
    const providerConfig = {
      client: new CognitoIdentityClient({ region }),
      identityPoolId,
    };
    
    // Include user tokens for authenticated access
    const loginKey = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
    providerConfig.logins = {
      [loginKey]: idToken
    };
    
    // Create credential provider and get credentials
    const credentialsProvider = fromCognitoIdentityPool(providerConfig);
    const credentials = await credentialsProvider();
    
    // Cache the credentials and their expiration time
    cacheCredentials(credentials);
    
    return credentials;
  } catch (error) {
    console.error('Failed to get temporary credentials:', error);
    throw error;
  }
}

/**
 * Helper function to retrieve authentication tokens from storage
 * Prioritizes localStorage for persistence across sessions
 * @returns {Object|null} Object containing tokens or null if not found
 */
function getAuthTokens() {
  // First check localStorage for persistent tokens
  if (localStorage.getItem('idToken')) {
    return {
      idToken: localStorage.getItem('idToken'),
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken')
    };
  } 
  // Fallback to sessionStorage
  else if (sessionStorage.getItem('idToken')) {
    return {
      idToken: sessionStorage.getItem('idToken'),
      accessToken: sessionStorage.getItem('accessToken'),
      refreshToken: sessionStorage.getItem('refreshToken')
    };
  }
  
  // If using memory storage, you'd add logic to retrieve from there
  return null;
}

/**
 * Check if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @param {number} bufferSeconds - Buffer time in seconds (default: 300 = 5 minutes)
 * @returns {boolean} - Whether the token is expired or will expire within buffer
 */
function isTokenExpired(token, bufferSeconds = 300) {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp <= currentTime + bufferSeconds;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
}

/**
 * Check if AWS authentication is configured
 * @returns {boolean} - Whether authentication is configured
 */
export function isAwsAuthConfigured() {
  return Boolean(AuthConfig.identityPoolId && AuthConfig.region);
}

// Expiration time in milliseconds (default: 55 minutes)
// Cognito credentials typically last 1 hour, we refresh slightly earlier
const CREDENTIALS_EXPIRATION = 55 * 60 * 1000;

// Cache for credentials
let cachedCredentials = null;
let credentialsExpiration = 0;

/**
 * Cache AWS credentials to minimize unnecessary calls
 * @param {Object} credentials - AWS credentials to cache
 */
function cacheCredentials(credentials) {
  cachedCredentials = credentials;
  
  // Calculate expiration time (use credential expiration if available, otherwise default)
  if (credentials.expiration) {
    // Subtract 5 minutes (300,000ms) as a safety buffer
    credentialsExpiration = credentials.expiration.getTime() - 300000;
  } else {
    credentialsExpiration = Date.now() + CREDENTIALS_EXPIRATION;
  }
  
  console.log('Credentials cached until:', new Date(credentialsExpiration).toISOString());
}

/**
 * Get AWS credentials with auto-refresh capability
 * @param {Object} options - Configuration options
 * @returns {Promise<Object>} - Refreshed credentials
 */
export async function getRefreshableCredentials(options = {}) {
  const currentTime = Date.now();
  
  // Check if we need to refresh credentials
  if (!cachedCredentials || currentTime >= credentialsExpiration) {
    // Get new credentials
    cachedCredentials = await getTemporaryCredentials(options);
    
    // Update the expiration time based on new credentials
    if (cachedCredentials.expiration) {
      credentialsExpiration = cachedCredentials.expiration.getTime() - (5 * 60 * 1000); // 5 min buffer
    } else {
      credentialsExpiration = currentTime + CREDENTIALS_EXPIRATION;
    }
  }
  
  return cachedCredentials;
}